import { ArrowDown, ArrowUp, Compass, Gauge, Navigation, Ruler, Thermometer, Timer, Waves, Wind } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useGet } from "@/lib/api";
import { beaufort, fmt, windDir } from "@/lib/meta";
import { de } from "@/lib/coast";

const until = (iso) => {
  const mins = Math.max(0, Math.round((new Date(iso) - new Date()) / 60000));
  return mins >= 60 ? `${Math.floor(mins / 60)} h ${mins % 60} min` : `${mins} min`;
};

const Stat = ({ Icon, label, value, testid }) => (
  <div className="bg-ocean px-3 py-2.5">
    <p className="flex items-center gap-1 text-[0.65rem] uppercase tracking-wider text-shell/55"><Icon size={11} /> {label}</p>
    <p className="mt-0.5 font-sub text-base leading-tight" data-testid={testid}>{value ?? "–"}</p>
  </div>
);

export const TideWidget = () => {
  const { data, isLoading, isError } = useGet("sea", "/sea");
  const { data: weather } = useGet("weather", "/weather");
  const c = data?.current || {};
  const wind = weather?.[0]?.current;
  const next = data?.tides?.[0];
  return (
    <div className="relative h-full overflow-hidden bg-ocean p-5 text-shell grain" data-testid="tide-widget">
      <div className="relative">
        <div className="flex items-baseline justify-between gap-3">
          <div>
            <p className="eyebrow text-sage">Wattenmeer live</p>
            <h3 className="mt-1 font-display text-2xl">Ebbe & Flut</h3>
          </div>
          {data?.trend && (
            <span className="flex items-center gap-1 border border-white/20 px-2 py-1 font-sub text-xs" data-testid="tide-trend">
              {data.trend === "rising" ? <ArrowUp size={12} className="text-sage" /> : <ArrowDown size={12} className="text-terracotta" />}
              {data.trend === "rising" ? "Flut läuft auf" : "Ebbe läuft ab"}
            </span>
          )}
        </div>
        {isLoading && <Skeleton className="mt-4 h-40 bg-white/10" />}
        {isError && <p className="mt-4 text-sm text-shell/70" data-testid="tide-error">Meeresdaten sind gerade nicht verfügbar.</p>}
        {data && (
          <>
            {next && (
              <p className="mt-3 flex items-center gap-1.5 text-sm text-shell/85" data-testid="tide-next">
                <Timer size={14} className="text-sand" />
                {next.type === "high" ? "Hochwasser" : "Niedrigwasser"} in <strong className="font-medium">{until(next.time)}</strong>
              </p>
            )}
            <div className="mt-3 grid grid-cols-3 gap-px bg-white/15">
              <Stat Icon={Thermometer} label="Wasser" value={c.sea_surface_temperature != null ? `${de(c.sea_surface_temperature)}°` : null} testid="sea-temp" />
              <Stat Icon={Waves} label="Wellen" value={c.wave_height != null ? `${de(c.wave_height)} m` : null} testid="wave-height" />
              <Stat Icon={Gauge} label="Periode" value={c.wave_period != null ? `${c.wave_period.toFixed(0)} s` : null} testid="wave-period" />
              <Stat Icon={Ruler} label="Pegel (Modell)" value={data.level_now != null ? `${data.level_now > 0 ? "+" : ""}${de(data.level_now, 2)} m` : null} testid="sea-level" />
              <Stat Icon={Navigation} label="Strömung" value={c.ocean_current_velocity != null ? `${de(c.ocean_current_velocity)} km/h ${windDir(c.ocean_current_direction)}` : null} testid="current-speed" />
              <Stat Icon={Wind} label="Wind" value={wind ? `${windDir(wind.wind_direction_10m)} ${beaufort(wind.wind_speed_10m)} Bft` : null} testid="tide-wind" />
            </div>
            <ul className="mt-3 grid grid-cols-2 gap-x-4 text-sm" data-testid="tide-list">
              {data.tides.slice(0, 4).map((t) => (
                <li key={t.time} className="flex items-center justify-between border-b border-white/10 py-1.5">
                  <span className="flex items-center gap-1.5 font-sub">
                    {t.type === "high" ? <ArrowUp size={13} className="text-sage" /> : <ArrowDown size={13} className="text-terracotta" />}
                    {t.type === "high" ? "HW" : "NW"}
                  </span>
                  <span className="text-shell/80">{fmt(t.time, "EEE")} {fmt(t.time, "HH:mm")}</span>
                </li>
              ))}
            </ul>
            <p className="mt-2 flex items-center gap-1.5 text-xs text-shell/60" data-testid="tide-extra">
              <Compass size={12} /> Dünung {c.swell_wave_height != null ? `${de(c.swell_wave_height)} m` : "–"}
              {c.wave_direction != null && ` aus ${windDir(c.wave_direction)}`}
              {data.tidal_range != null && ` · Tidenhub ca. ${de(data.tidal_range)} m`}
            </p>
            <p className="mt-2 text-[0.65rem] leading-snug text-shell/45">
              Modellwerte (Open-Meteo), {data.location}, ±30 Min. Nicht für Navigation oder Wattwanderungen –{" "}
              <a href="https://www.bsh.de/DE/DATEN/Vorhersagen/Gezeiten/gezeiten_node.html" target="_blank" rel="noreferrer" className="underline">offizielle BSH-Gezeiten</a>.
            </p>
          </>
        )}
      </div>
    </div>
  );
};
